// src/Views/HowToJoin.js
import React, { useState } from "react";
import { Link } from "react-router-dom";
import Header from "./Header.js";
import Footer from "./Footer.js";
import TurnstileField from "../components/TurnstileField";
import { contactApiUrl } from "../auth/api";

function HowToJoin() {
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileKey, setTurnstileKey] = useState(0);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!turnstileToken) {
      setMessage({
        text: "Please complete the Turnstile challenge",
        type: "error",
      });
      return;
    }

    setFormSubmitting(true);

    try {
      const formData = {
        name: e.target.elements.name.value,
        email: e.target.elements.email.value,
        shootingExperience: e.target.elements.shootingExperience.value,
        otherClubMember: e.target.elements.otherClubMember.value,
        firearmsCertificate: e.target.elements.firearmsCertificate.value,
        interests: e.target.elements.interests.value,
        location: e.target.elements.location.value,
        turnstileToken: turnstileToken,
        formType: "membership-inquiry",
      };

      const response = await fetch(contactApiUrl(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error("Failed to send message");
      }

      setMessage({
        text: "Application submitted successfully!",
        type: "success",
      });
      e.target.reset();
    } catch (error) {
      console.error("Error sending message:", error);
      setMessage({
        text: "Failed to send application. Please try again later.",
        type: "error",
      });
    } finally {
      setFormSubmitting(false);
      setTurnstileToken(null);
      setTurnstileKey((k) => k + 1);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <Header />
      <div className="bg-green-50 py-16" id="how-to-join">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-green-800 mb-8">
            How to Join
          </h2>
          <p className="text-lg text-center text-green-700 max-w-3xl mx-auto mb-8">
            Due to the nature of our sport becoming a member is not as
            straightforward as other club sports.
          </p>
          <div className="bg-yellow-200 border-4 border-yellow-500 rounded-lg p-6 max-w-3xl mx-auto mb-8">
            <p className="text-xl font-bold text-center text-yellow-900">
              Membership is currently by invitation or member introduction only.
            </p>
          </div>
          <p className="text-lg text-center text-green-700 max-w-3xl mx-auto mb-8">
            We periodically accept new novice members to commence the membership
            process so if you are interested in being considered, please
            complete the form below. New to practical shotgun? Read the{" "}
            <Link to="/novice-guide" className="underline font-semibold">
              novice guide
            </Link>{" "}
            for the joining process.
          </p>
          <p className="text-lg text-center text-green-700 max-w-3xl mx-auto mb-8 italic">
            Full members of other clubs may be eligible for an alternative
            accelerated process.
          </p>
          <p className="text-lg text-center text-green-700 max-w-3xl mx-auto mb-8">
            Please provide answers to ALL the questions.
          </p>
          {message.text && (
            <div
              className={`mb-4 p-3 rounded text-center max-w-2xl mx-auto ${
                message.type === "success"
                  ? "bg-green-200 text-green-800"
                  : "bg-red-200 text-red-800"
              }`}
            >
              {message.text}
            </div>
          )}
          <form className="max-w-2xl mx-auto" onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-left text-green-800 font-semibold mb-2">
                Your Name *
              </label>
              <input
                type="text"
                name="name"
                placeholder="Your Name"
                className="w-full p-2 border border-green-300 rounded"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-left text-green-800 font-semibold mb-2">
                Your Email *
              </label>
              <input
                type="email"
                name="email"
                placeholder="Your Email"
                className="w-full p-2 border border-green-300 rounded"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-left text-green-800 font-semibold mb-2">
                Do you have any previous shooting experience? *
              </label>
              <textarea
                name="shootingExperience"
                placeholder="Please describe your shooting experience"
                className="w-full p-2 border border-green-300 rounded h-24"
                required
              ></textarea>
            </div>
            <div className="mb-4">
              <label className="block text-left text-green-800 font-semibold mb-2">
                Are you a member of another shooting club in the UK or abroad? *
              </label>
              <textarea
                name="otherClubMember"
                placeholder="Please provide details about any club memberships"
                className="w-full p-2 border border-green-300 rounded h-24"
                required
              ></textarea>
            </div>
            <div className="mb-4">
              <label className="block text-left text-green-800 font-semibold mb-2">
                Do you currently hold a Firearms Certificate or Shotgun
                Certificate? *
              </label>
              <textarea
                name="firearmsCertificate"
                placeholder="Please provide details about any certificates you hold"
                className="w-full p-2 border border-green-300 rounded h-24"
                required
              ></textarea>
            </div>
            <div className="mb-4">
              <label className="block text-left text-green-800 font-semibold mb-2">
                What interests you about our club and the Practical Shooting
                sport? *
              </label>
              <textarea
                name="interests"
                placeholder="Please tell us what interests you"
                className="w-full p-2 border border-green-300 rounded h-32"
                required
              ></textarea>
            </div>
            <div className="mb-4">
              <label className="block text-left text-green-800 font-semibold mb-2">
                Where are you based? *
              </label>
              <input
                type="text"
                name="location"
                placeholder="Your location"
                className="w-full p-2 border border-green-300 rounded"
                required
              />
            </div>
            <div className="mb-4 flex justify-center">
              <TurnstileField
                key={turnstileKey}
                onVerify={(token) => setTurnstileToken(token)}
                onExpire={() => setTurnstileToken(null)}
                onError={() => setTurnstileToken(null)}
              />
            </div>
            <button
              type="submit"
              className="w-full bg-green-700 text-white py-2 px-4 rounded hover:bg-green-600 transition-colors disabled:bg-gray-400"
              disabled={formSubmitting || !turnstileToken}
            >
              {formSubmitting ? "Submitting..." : "Submit Membership Enquiry"}
            </button>
            <div className="mt-4 text-center">
              <a
                href="/"
                className="text-green-700 hover:text-green-800 underline"
              >
                ← Back to Home
              </a>
            </div>
          </form>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default HowToJoin;
